import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Crown, FileText, Download, Sparkles, Shield, Check } from 'lucide-react';
import { Button } from '../components/ui/button';

const BENEFITS = [
  { icon: FileText, text: '6 professional CVs (vs 1)' },
  { icon: Download, text: 'Clean PDFs — no watermark' },
  { icon: Sparkles, text: 'Unlimited AI enhancements' },
  { icon: Shield, text: 'All premium templates' },
];

export default function UpgradeModal({ isOpen, onClose, trigger = 'default' }) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const headlines = {
    watermark: {
      title: 'Your CV Has a Watermark',
      subtitle: 'Employers may not take watermarked CVs seriously. Remove it for just R25.',
    },
    cv_limit: {
      title: 'You\'ve Reached Your Free CV Limit',
      subtitle: 'Free accounts can create 1 CV. Upgrade to create up to 6 different CVs.',
    },
    ai_feature: {
      title: 'This is a Premium Feature',
      subtitle: 'Unlock AI-powered tools to make your CV stand out to employers.',
    },
    default: {
      title: 'Upgrade to Career Step Pro',
      subtitle: 'Get everything you need to land your dream job.',
    },
  };

  const { title, subtitle } = headlines[trigger] || headlines.default;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative w-full max-w-md bg-gradient-to-b from-slate-900 to-slate-800 rounded-2xl shadow-2xl border border-slate-700/50 overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        data-testid="upgrade-modal"
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors z-10"
          data-testid="upgrade-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header accent */}
        <div className="h-1.5 bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500" />

        <div className="p-6 pt-5">
          {/* Icon */}
          <div className="w-14 h-14 rounded-xl bg-amber-500/15 flex items-center justify-center mb-4 mx-auto">
            <Crown className="w-7 h-7 text-amber-400" />
          </div>

          {/* Headline */}
          <h2
            className="text-xl font-bold text-white text-center mb-1"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="upgrade-modal-title"
          >
            {title}
          </h2>
          <p className="text-slate-400 text-sm text-center mb-6">{subtitle}</p>

          {/* Benefits */}
          <div className="space-y-3 mb-6">
            {BENEFITS.map((b, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-emerald-400" />
                </div>
                <span className="text-slate-200 text-sm">{b.text}</span>
              </div>
            ))}
          </div>

          {/* Price */}
          <div className="text-center mb-5">
            <span className="text-slate-400 text-sm">Only </span>
            <span className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>R25</span>
            <span className="text-slate-400 text-sm"> once-off</span>
            <p className="text-slate-500 text-xs mt-1">30 days full access — less than a data bundle</p>
          </div>

          {/* CTA */}
          <Button
            onClick={() => {
              onClose();
              navigate('/subscribe');
            }}
            className="w-full py-6 text-base font-semibold bg-amber-500 hover:bg-amber-600 text-white rounded-xl shadow-lg shadow-amber-500/20 transition-all"
            data-testid="upgrade-modal-cta"
          >
            <Crown className="w-5 h-5 mr-2" />
            Upgrade Now — R25 once-off
          </Button>

          <button
            onClick={onClose}
            className="w-full mt-3 text-sm text-slate-500 hover:text-slate-300 transition-colors text-center py-2"
            data-testid="upgrade-modal-dismiss"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
